import type { DeliveryZone, DeliveryZonePoint } from '@choco-admin/types';

/**
 * The predefined delivery areas legacy admin calls the "zone library".
 *
 * Each country has one file of ready-made regions, so CS can pick "Greater
 * London" from a list instead of tracing it on a map. The files live in
 * `<env>-delivery-zone-library` in S3, one per locale, named
 * `<locale>.json.gzip` in lowercase. Legacy reads them the same way
 * (`fetchDeliveryZones.ts`), through a presigned URL.
 *
 * All of that is confirmed against a live request from legacy admin on staging:
 * `chostg-delivery-zone-library.s3.eu-west-1.amazonaws.com/fr-fr.json.gzip`,
 * 17 French regions, 162 kB.
 *
 * Despite the name and a `content-type: application/x-gzip`, that file is NOT
 * gzipped: it begins `[` and parses as JSON directly, and the response carries no
 * `content-encoding`. So the plain path is the live one and the inflate fallback
 * in `readLibraryResponse` never fires today. It stays as insurance, because
 * per-object metadata is not a guarantee across every locale file.
 */

/** One region as the library stores it. */
export interface LibraryZone {
  /**
   * A number in the real files, not a string. Confirmed against
   * `chostg-delivery-zone-library/fr-fr.json.gzip`, where France's entries carry
   * `"region": 2`. Nothing here reads it; it is kept so the shape stays honest.
   */
  region: number;
  country: string;
  locale: string;
  nameEnglish: string;
  nameLocalised: string;
  points: DeliveryZonePoint[];
}

/**
 * The countries with a library file, mirroring legacy's `supportedLocales`.
 *
 * Kept as an explicit list rather than derived from `LOCALE_OPTIONS`, because a
 * distributor locale existing does not mean a library file exists for it. A
 * locale absent here has no file, and a locale present here can still 404 if the
 * file was never uploaded to this environment.
 */
export const LIBRARY_COUNTRIES: ReadonlyArray<{
  locale: string;
  label: string;
}> = [
  { locale: 'de-DE', label: 'Germany & Austria' },
  { locale: 'en-GB', label: 'United Kingdom' },
  { locale: 'en-AE', label: 'UAE' },
  { locale: 'ar-SA', label: 'Saudi Arabia (Arabic)' },
  { locale: 'en-SA', label: 'Saudi Arabia (English)' },
  { locale: 'en-US', label: 'United States' },
  { locale: 'en-CA', label: 'Canada (English)' },
  { locale: 'es-ES', label: 'Spain' },
  { locale: 'fr-FR', label: 'France' },
  { locale: 'nl-BE', label: 'Belgium' },
];

/** Where the picker starts: the distributor's own country when we have it. */
export const DEFAULT_LIBRARY_LOCALE = 'de-DE';

export const resolveLibraryLocale = (
  supplierLocale?: string | null
): string => {
  const supported = LIBRARY_COUNTRIES.some(
    (country) => country.locale === supplierLocale
  );
  return supported && supplierLocale ? supplierLocale : DEFAULT_LIBRARY_LOCALE;
};

/** The S3 object key for a country's library file. */
export const libraryObjectPath = (locale: string): string =>
  `${locale.toLowerCase()}.json.gzip`;

/** The per-environment bucket holding those files. */
export const libraryBucketName = (environmentKey: string): string =>
  `${environmentKey}-delivery-zone-library`;

/**
 * Strip accents and punctuation so a search for "koln" matches "Köln".
 * Mirrors legacy's `tokenize`.
 */
const tokenize = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[\W_\s]+/g, ' ')
    .toLowerCase()
    .trim();

export const filterLibraryZonesByName = (
  searchTerm: string,
  zones: LibraryZone[]
): LibraryZone[] => {
  const term = tokenize(searchTerm);
  if (!term) return zones;

  // Both names are searched, because CS may know a region by either.
  return zones.filter(
    (zone) =>
      tokenize(zone.nameLocalised).includes(term) ||
      tokenize(zone.nameEnglish).includes(term)
  );
};

const isUsableZone = (value: unknown): value is LibraryZone => {
  if (typeof value !== 'object' || value === null) return false;

  const name = Reflect.get(value, 'nameLocalised');
  const points = Reflect.get(value, 'points');

  return (
    typeof name === 'string' &&
    name.trim().length > 0 &&
    Array.isArray(points) &&
    points.length >= 3
  );
};

/**
 * Read a library file's contents.
 *
 * The file is remote reference data that nothing in this repo produces, so it is
 * treated as untrusted: entries without a usable name or without enough points
 * to enclose an area are dropped rather than allowed to reach the map or a save.
 *
 * Throws when the text is not JSON at all, which is the signal to try inflating
 * it (see `readLibraryResponse`).
 */
export const parseLibraryJson = (text: string): LibraryZone[] => {
  const parsed: unknown = JSON.parse(text);
  if (!Array.isArray(parsed)) return [];

  return parsed.filter(isUsableZone).map((zone) => ({
    ...zone,
    nameEnglish: zone.nameEnglish || zone.nameLocalised,
  }));
};

/**
 * Fetch-response wrapper for the above.
 *
 * The objects are gzipped. S3 normally serves them with `Content-Encoding: gzip`
 * so the browser inflates them and the body reads as JSON directly, which is what
 * legacy relies on. That header is object metadata rather than a guarantee, so a
 * body that does not parse is inflated here instead of failing.
 *
 * Browser-only, so it carries no logic worth unit-testing: everything that can be
 * decided without a network stack lives in `parseLibraryJson`.
 */
export const readLibraryResponse = async (
  response: Response
): Promise<LibraryZone[]> => {
  const buffer = await response.arrayBuffer();

  try {
    return parseLibraryJson(new TextDecoder().decode(buffer));
  } catch {
    const inflated = new Blob([buffer])
      .stream()
      .pipeThrough(new DecompressionStream('gzip'));
    return parseLibraryJson(await new Response(inflated).text());
  }
};

/**
 * Turn picked library regions into delivery areas.
 *
 * The localised name is used, matching legacy, so a German distributor's areas
 * read in German. Names already taken are skipped rather than added and then
 * rejected: zone names must be unique, and the backend fails the whole save on a
 * duplicate.
 */
export const toDeliveryZonesFromLibrary = ({
  selected,
  existingZones,
}: {
  selected: LibraryZone[];
  existingZones: DeliveryZone[];
}): { zones: DeliveryZone[]; skippedNames: string[] } => {
  const taken = new Set(
    existingZones.map((zone) => zone.name.trim().toLowerCase())
  );
  const zones: DeliveryZone[] = [];
  const skippedNames: string[] = [];

  selected.forEach((libraryZone) => {
    const name = libraryZone.nameLocalised.trim();
    const key = name.toLowerCase();

    if (taken.has(key)) {
      skippedNames.push(name);
      return;
    }

    taken.add(key);
    zones.push({ name, points: libraryZone.points });
  });

  return { zones, skippedNames };
};
